import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { type Language, resolveLocale, setLocale } from '@/shared/i18n';

export type UserType = 'EMPLOYEE_PORTAL_USER' | 'CUSTOMER_PORTAL_USER';

export interface CurrentUser {
  username: string;
  email: string;
  extension: string | null;
  roles: string[];
  userType: UserType;
  /**
   * The user's explicitly chosen UI language, or `null` if they have never
   * picked one. When `null` the UI falls back to the browser language; a
   * browser-derived value is never persisted here.
   */
  language: Language | null;
  /**
   * True when the account is backed by the external directory (LDAP) and the
   * password is therefore managed there — the in-app password-change form is
   * hidden for such users. Optional until the backend `/me` endpoint populates it.
   */
  ldap?: boolean;
}

const TOKEN_KEY = 's4pconnect.token';
const USER_KEY = 's4pconnect.user';

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(sessionStorage.getItem(TOKEN_KEY));
  const userRaw = sessionStorage.getItem(USER_KEY);
  const user = ref<CurrentUser | null>(userRaw ? (JSON.parse(userRaw) as CurrentUser) : null);

  const isAuthenticated = computed(() => token.value !== null && user.value !== null);
  const userType = computed<UserType | null>(() => user.value?.userType ?? null);

  function set(newToken: string, newUser: CurrentUser): void {
    token.value = newToken;
    user.value = newUser;
    sessionStorage.setItem(TOKEN_KEY, newToken);
    sessionStorage.setItem(USER_KEY, JSON.stringify(newUser));
    // Apply the signed-in user's language (or fall back to the browser).
    setLocale(resolveLocale(newUser.language));
  }

  /**
   * Replaces just the access token, keeping the current user. Called after a
   * silent token refresh (the refresh endpoint returns a new access token but
   * no user payload).
   */
  function setToken(newToken: string): void {
    token.value = newToken;
    sessionStorage.setItem(TOKEN_KEY, newToken);
  }

  /**
   * Updates the persisted language for the current user (called after the
   * backend accepts a change). Locale switching is the caller's concern.
   */
  function setLanguage(language: Language): void {
    if (user.value === null) return;
    user.value.language = language;
    sessionStorage.setItem(USER_KEY, JSON.stringify(user.value));
  }

  /**
   * Mirrors the saved extension back into the cached user so views bound to the
   * store (e.g. the profile summary) reflect a profile save without a reload.
   */
  function setExtension(extension: string | null): void {
    if (user.value === null) return;
    user.value.extension = extension;
    sessionStorage.setItem(USER_KEY, JSON.stringify(user.value));
  }

  function clear(): void {
    token.value = null;
    user.value = null;
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);
    // No user → fall back to the browser language for the login screen.
    setLocale(resolveLocale(null));
  }

  return {token, user, isAuthenticated, userType, set, setToken, setLanguage, setExtension, clear};
});
